import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { useGeofences } from '@/hooks/use-geofences';
import { toast } from 'sonner';
import { GooglePlacesAutocomplete } from '@/components/fleet/GooglePlacesAutocomplete';
import '@/components/fleet/GooglePlacesAutocomplete.css';

interface GeofenceFormProps {
  companyId?: string;
  geofenceId?: string | null;
  onBack?: () => void;
  onCenterMap?: (position: { lat: number; lng: number }) => void;
  onEnableDrawing?: (type: 'circle' | 'polygon') => void;
  drawnGeofence?: any;
}

export function GeofenceForm({ 
  companyId, 
  geofenceId,
  onBack,
  onCenterMap,
  onEnableDrawing,
  drawnGeofence
}: GeofenceFormProps) {
  const { 
    geofences, 
    createGeofence, 
    updateGeofence,
    isCreating,
    isUpdating 
  } = useGeofences(companyId);

  const [formData, setFormData] = useState({
    name: '',
    type: 'circle' as 'circle' | 'polygon',
    address: '',
    max_speed_kmh: null as number | null,
    alert_on_entry: true,
    alert_on_exit: true,
    active: true,
  });

  const [speedControlEnabled, setSpeedControlEnabled] = useState(false);
  const [selectedCoordinates, setSelectedCoordinates] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  // Carregar dados se estiver editando
  useEffect(() => {
    if (geofenceId) {
      const geofence = geofences.find(g => g.id === geofenceId);
      if (geofence) {
        setFormData({
          name: geofence.name,
          type: geofence.type,
          address: '',
          max_speed_kmh: null, // TODO: adicionar ao schema
          alert_on_entry: geofence.alert_on_entry,
          alert_on_exit: geofence.alert_on_exit,
          active: geofence.active,
        });
      }
    }
  }, [geofenceId, geofences]);

  const handlePlaceSelect = (place: {
    address: string;
    lat: number;
    lng: number;
    place_id: string;
  }) => {
    console.log('📍 Place selecionado:', place);
    
    // Atualizar endereço no formulário
    setFormData(prev => ({ ...prev, address: place.address }));
    
    // Salvar coordenadas
    setSelectedCoordinates({ lat: place.lat, lng: place.lng });
    
    // Centralizar mapa na posição
    if (onCenterMap) {
      onCenterMap({ lat: place.lat, lng: place.lng });
    }
    
    // Ativar modo de desenho
    if (onEnableDrawing) {
      onEnableDrawing(formData.type as 'circle' | 'polygon');
    }
    
    toast.success('Mapa centralizado! Desenhe a geocerca no mapa.');
  };

  // Atualizar coordenadas quando geocerca for desenhada
  useEffect(() => {
    if (drawnGeofence) {
      console.log('📍 Geocerca desenhada recebida:', drawnGeofence);
      toast.success('Geocerca desenhada! Preencha os dados e salve.');
    }
  }, [drawnGeofence]);

  // Trocar tipo de geocerca (círculo/polígono)
  const handleTypeChange = (newType: 'circle' | 'polygon') => {
    setFormData(prev => ({ ...prev, type: newType }));
    // Reset modo desenho se já estava ativo
    if (onEnableDrawing) {
      onEnableDrawing('none' as any);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Nome da cerca é obrigatório');
      return;
    }

    if (!companyId) {
      toast.error('Empresa não identificada');
      return;
    }

    if (!drawnGeofence) {
      toast.error('Desenhe a geocerca no mapa antes de salvar!');
      return;
    }

    // Usar coordenadas reais desenhadas no mapa
    const geofenceData = {
      company_id: companyId,
      name: formData.name,
      type: formData.type as 'circle' | 'polygon',
      coordinates: formData.type === 'circle' 
        ? {
            lat: drawnGeofence.center.lat,
            lng: drawnGeofence.center.lng,
            radius: Math.round(drawnGeofence.radius),
          }
        : (drawnGeofence.coordinates || []).map((c: any) => ({ lat: c.lat, lng: c.lng })),
      color: '#3B82F6',
      active: formData.active,
      alert_on_entry: formData.alert_on_entry,
      alert_on_exit: formData.alert_on_exit,
    };

    if (geofenceId) {
      updateGeofence({ id: geofenceId, ...geofenceData });
    } else {
      createGeofence(geofenceData);
    }

    onBack();
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b p-6">
        <Button
          variant="ghost"
          onClick={onBack}
          className="mb-4 -ml-2"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {geofenceId ? 'Editar Cerca' : 'Nova Cerca'}
        </Button>
      </div>

      {/* Form */}
      <ScrollArea className="flex-1">
        <div className="p-6 space-y-6">
          {/* Nome da cerca */}
          <div className="space-y-2">
            <Label htmlFor="name">Nome da cerca *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Ex: Matriz São Paulo"
            />
          </div>

          {/* Endereço para busca */}
          <div className="space-y-2">
            <Label htmlFor="address">Endereço</Label>
            <GooglePlacesAutocomplete
              value={formData.address}
              onChange={(address) => setFormData(prev => ({ ...prev, address }))}
              onPlaceSelect={handlePlaceSelect}
              placeholder="Digite o endereço para centralizar o mapa..."
              disabled={false}
            />
            <p className="text-xs text-muted-foreground">
              Selecione um endereço para centralizar o mapa e desenhar a cerca
            </p>
          </div>

          {/* Indicador visual de modo desenho ativo */}
          {selectedCoordinates && (
            <div className="p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-md">
              <p className="text-sm text-blue-700 dark:text-blue-300 font-medium flex items-center gap-2">
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                {formData.type === 'circle' 
                  ? 'Mapa centralizado - Clique no mapa para desenhar o círculo' 
                  : 'Mapa centralizado - Clique no mapa para adicionar vértices'}
              </p>
            </div>
          )}

          {/* Tipo de cerca */}
          <div className="space-y-2">
            <Label htmlFor="type">Tipo de cerca *</Label>
            <Select
              value={formData.type}
              onValueChange={handleTypeChange}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="circle">Círculo</SelectItem>
                <SelectItem value="polygon">Polígono</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {formData.type === 'circle' 
                ? 'Clique no mapa para definir o centro e arraste para definir o raio'
                : 'Clique no mapa para adicionar vértices. Clique duplo para finalizar.'
              }
            </p>
          </div>

          {/* Velocidade máxima */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="speed-control">Definir velocidade máxima na cerca</Label>
              <Switch
                id="speed-control"
                checked={speedControlEnabled}
                onCheckedChange={setSpeedControlEnabled}
              />
            </div>
            
            {speedControlEnabled && (
              <div className="space-y-2">
                <Label htmlFor="max-speed">Velocidade máxima (km/h)</Label>
                <Input
                  id="max-speed"
                  type="number"
                  min="0"
                  value={formData.max_speed_kmh || ''}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    max_speed_kmh: e.target.value ? parseInt(e.target.value) : null 
                  })}
                  placeholder="Ex: 60"
                />
              </div>
            )}
          </div>

          {/* Alertas */}
          <div className="space-y-4 pt-4 border-t">
            <h4 className="font-medium">Alertas</h4>
            
            <div className="flex items-center justify-between">
              <Label htmlFor="alert-entry">Alertar ao entrar na cerca</Label>
              <Switch
                id="alert-entry"
                checked={formData.alert_on_entry}
                onCheckedChange={(checked) => 
                  setFormData({ ...formData, alert_on_entry: checked })
                }
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="alert-exit">Alertar ao sair da cerca</Label>
              <Switch
                id="alert-exit"
                checked={formData.alert_on_exit}
                onCheckedChange={(checked) => 
                  setFormData({ ...formData, alert_on_exit: checked })
                }
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="active">Cerca ativa</Label>
              <Switch
                id="active"
                checked={formData.active}
                onCheckedChange={(checked) => 
                  setFormData({ ...formData, active: checked })
                }
              />
            </div>
          </div>
        </div>
      </ScrollArea>

      {/* Footer com botões */}
      <div className="border-t p-6 space-y-3">
        <Button
          variant="outline"
          className="w-full"
          onClick={() => {
            // TODO: Limpar desenho do mapa
            toast.info('Desenho da cerca limpo');
          }}
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Limpar cerca
        </Button>
        
        <Button
          className="w-full"
          onClick={handleSubmit}
          disabled={isCreating || isUpdating}
        >
          {(isCreating || isUpdating) ? 'Salvando...' : 'Salvar →'}
        </Button>
      </div>
    </div>
  );
}
